import { useMemo } from 'react';
import { format, parseISO, subDays, isAfter } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { JobRun } from '../../types/jobs';

interface JobCostChartProps {
  runs: JobRun[];
}

interface DayData {
  date: string;
  cost: number;
  duration: number;
}

const TOOLTIP_STYLE = {
  backgroundColor: '#1a1a24',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '6px',
  fontSize: '12px',
};

function CustomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DayData }> }) {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0].payload;
  return (
    <div style={TOOLTIP_STYLE} className="p-2 text-text-primary">
      <p className="text-xs text-text-secondary mb-1">{data.date}</p>
      <p className="text-sm font-medium">${data.cost.toFixed(2)}</p>
      <p className="text-xs text-text-muted">{formatMs(data.duration)}</p>
    </div>
  );
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

export default function JobCostChart({ runs }: JobCostChartProps) {
  const data = useMemo(() => {
    const cutoff = subDays(new Date(), 30);
    const byDay = new Map<string, { cost: number; duration: number }>();

    for (const run of runs) {
      const date = parseISO(run.started_at);
      if (!isAfter(date, cutoff)) continue;

      const key = format(date, 'MM/dd');
      const existing = byDay.get(key) ?? { cost: 0, duration: 0 };
      existing.cost += run.cost_usd;
      existing.duration += run.duration_ms ?? 0;
      byDay.set(key, existing);
    }

    const result: DayData[] = [];
    for (const [date, vals] of byDay) {
      result.push({ date, cost: parseFloat(vals.cost.toFixed(2)), duration: vals.duration });
    }

    return result;
  }, [runs]);

  if (data.length === 0) {
    return <p className="text-sm text-text-muted text-center py-8">No cost data in the last 30 days.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
        <XAxis
          dataKey="date"
          tick={{ fill: '#64748b', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fill: '#64748b', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v: number) => `$${v}`}
          width={45}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(124,58,237,0.1)' }} />
        <Bar dataKey="cost" fill="#7c3aed" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
